import React from 'react'
import { Grid } from '@paintscout/ui'
import {
  EstimatorSelect,
  DateRangePicker,
  TagSelection,
  DropdownSelect,
  useClientOptions,
  useUserPermissions
} from '@paintscout/ui-business'
import type { DropdownSelectOption } from '@paintscout/ui-business'
import type { InFilter, ValueFilter } from 'json-to-lucene'
import { getFeatures, getLeadSources, getTags } from '@paintscout/util/builder'
import type { QuoteTag } from 'paintscout'
import { addAuth0Prefix, handleFilterChange, handleRangeFilterChange, removeAuth0Prefix } from '@paintscout/util'
import moment from 'moment'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { mapSourceToOptions, preferredContactMethodOptions } from '../ContactSearchBar/ContactSearchFilters'

export interface CompanySearchFiltersProps {
  classes?: { root?: string }
  filters: ValueFilter[]
  onChange?: (filters: ValueFilter[]) => any
}

function CompanySearchFilters(props: CompanySearchFiltersProps) {
  const { permissionToViewEstimator } = useUserPermissions()
  const { options } = useClientOptions()
  const { filters, onChange } = props
  const { crm: crmFlag } = useFlags()
  const features = getFeatures({ options })
  const tagsEnabled = !!(features.quotes?.tags?.enabled || crmFlag)
  const hasSources = !!(features.contactLeadSources?.enabled || crmFlag)
  const lgWidth = (tagsEnabled && hasSources) || (!tagsEnabled && !hasSources) ? 6 : 4

  const sourceOptions = getLeadSources({ options, type: 'leadSources' })

  const filteredTags = getTags({
    options,
    keys: filters
      .filter((f) => f.id.startsWith('tags'))
      .map((f) => f.value)
      .flat()
  })

  const handleTagsChange = (newTags: QuoteTag[]) => {
    const tagFilter: ValueFilter = newTags.reduce(
      (acc, tag) => {
        return {
          ...acc,
          value: [...acc.value, tag.name]
        }
      },
      {
        id: `tags`,
        type: 'in',
        operator: 'AND',
        value: []
      }
    )

    if (onChange) {
      onChange([...filters.filter(({ id }) => id !== 'tags'), tagFilter])
    }
  }

  const dateFilter = filters.find((filter) => filter.id === 'created')
  const ownerFilter = filters.find(({ id }) => id === 'owner.id') as InFilter
  // Companies dont currently have a preferred contact method, but may once we have form design, so stubbing in
  const _filteredContactMethods = preferredContactMethodOptions.filter((source) => {
    const sourceFilter = filters.find(({ id }) => id === 'preferred_contact_method') as InFilter
    if (sourceFilter) {
      if (sourceFilter.value.length === 0) {
        return false // No filter applied
      } else {
        return sourceFilter.value.includes(source.value)
      }
    }
  })
  const filteredSources = sourceOptions.filter((source) => {
    const sourceFilter = filters.find(({ id }) => id === 'lead_source') as InFilter
    if (sourceFilter) {
      if (sourceFilter.value.length === 0) {
        return false // No filter applied
      } else {
        return sourceFilter.value.includes(source.label)
      }
    }
  })

  return (
    <Grid container spacing={2} alignItems="flex-end">
      <Grid item xs={12} lg={lgWidth}>
        <DateRangePicker
          startDate={dateFilter?.value[0] ? moment(dateFilter.value[0]) : null}
          endDate={dateFilter?.value[1] ? moment(dateFilter.value[1]) : null}
          onConfirm={(startDate, endDate) => {
            handleRangeFilterChange({
              id: 'created',
              start: startDate ? startDate.set('hour', 0).unix() * 1000 : null,
              end: endDate ? endDate.set('hour', 23).unix() * 1000 : null,
              filters,
              onChange
            })
          }}
          PopperProps={{
            placement: 'bottom-end'
          }}
        />
      </Grid>
      {permissionToViewEstimator && (
        <Grid item xs={12} lg={lgWidth}>
          <EstimatorSelect
            name="owner"
            label="Owner"
            searchable
            variant="multi"
            value={(typeof ownerFilter?.value === 'string' ? [ownerFilter?.value] : ownerFilter?.value)?.map((val) =>
              addAuth0Prefix(val as string)
            )}
            roles={['sales', 'admin']}
            placeholder={{
              selectPlaceholder: 'Any owner',
              plural: 'Owners'
            }}
            onChange={(options: DropdownSelectOption[]) => {
              handleFilterChange(
                {
                  id: 'owner.id',
                  type: 'in',
                  value: options.map((option) => removeAuth0Prefix(option.value))
                },
                filters,
                onChange
              )
            }}
            fullWidth
          />
        </Grid>
      )}
      {hasSources && (
        <Grid item xs={12} lg={lgWidth}>
          <DropdownSelect
            variant="multi"
            name="source"
            label={'Source'}
            placeholder={{
              selectPlaceholder: 'Select a source',
              plural: 'sources'
            }}
            fullWidth
            searchable
            value={mapSourceToOptions(filteredSources)}
            onChange={(option: DropdownSelectOption[]) => {
              const leadSourceFilter: ValueFilter = option.reduce(
                (acc, tag) => {
                  return {
                    ...acc,
                    value: [...acc.value, tag.label, tag.key]
                  }
                },
                {
                  id: `lead_source`,
                  type: 'in',
                  value: []
                }
              )
              if (onChange) {
                onChange([...filters.filter(({ id }) => id !== 'lead_source'), leadSourceFilter])
              }
            }}
            options={mapSourceToOptions(sourceOptions)}
            noOptionsMessage={sourceOptions.length !== 0 ? 'No options' : `You haven't created any lead sources`}
          />
        </Grid>
      )}
      {/* <Grid item xs={12} lg={4}>
        <DropdownSelect
          variant="multi"
          onChange={(option: DropdownSelectOption[]) => {
            const preferredContactMethodFilter: ValueFilter = option.reduce(
              (acc, tag) => {
                return {
                  ...acc,
                  value: [...acc.value, tag.value]
                }
              },
              {
                id: `preferred_contact_method`,
                type: 'in',
                value: []
              }
            )
            console.log({ preferredContactMethodFilter, option })
            if (onChange) {
              onChange([...filters.filter(({ id }) => id !== 'preferred_contact_method'), preferredContactMethodFilter])
            }
          }}
          label={'Preferred Contact Method'}
          fullWidth
          value={filteredContactMethods}
          options={preferredContactMethodOptions}
        />
      </Grid> */}
      {tagsEnabled && (
        <Grid item xs={12} lg={lgWidth}>
          <TagSelection label="Tags" appliedTags={filteredTags} fullWidth handleChange={handleTagsChange} />
        </Grid>
      )}
    </Grid>
  )
}

export default CompanySearchFilters
